import { useCallback, useEffect, useRef, useState } from 'react';

import { ROUTES } from '@/constants/routes';
import userProfileService from '@/services/user-profile-service';
import {
  clearAuthHint,
  clearStaleKeycloakCallbacks,
  getAuthHint,
  setAuthHint,
} from '@/utils/auth-hint';
import { withPostLoginRedirect } from '@/utils/auth-redirect';

export type PublicAuthStatus = {
  isAuthenticated: boolean;
  isChecking: boolean;
  hasResolved: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  picture?: string;
  isLoggingOut: boolean;
  login: () => void;
  logout: () => Promise<boolean>;
};

type PublicKeycloakClient = {
  authenticated?: boolean;
  idToken?: string;
  token?: string;
  tokenParsed?: unknown;
  init: (options: {
    pkceMethod: 'S256';
    checkLoginIframe: boolean;
    onLoad: 'check-sso';
    responseMode: 'query';
    silentCheckSsoRedirectUri: string;
    silentCheckSsoFallback: boolean;
  }) => Promise<boolean>;
  clearToken: () => void;
};

const CRAWLER_USER_AGENT =
  /bot|crawler|spider|crawling|google|bing|duckduckbot|slurp|yandex|baiduspider|facebookexternalhit|twitterbot|linkedinbot|whatsapp|telegrambot/i;

const isLikelyCrawler = () => {
  if (typeof navigator === 'undefined') return true;
  return CRAWLER_USER_AGENT.test(navigator.userAgent);
};

const isSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent;
  return (
    /safari/i.test(userAgent) &&
    !/chrome|chromium|crios|fxios|edg/i.test(userAgent)
  );
};

const runWhenIdle = (callback: () => void) => {
  if (typeof window === 'undefined') return 0;

  const handle = window.setTimeout(callback, 250);
  return () => window.clearTimeout(handle);
};

export const usePublicAuthStatus = (): PublicAuthStatus => {
  const [initialHint] = useState(() => getAuthHint());
  const shouldCheckAuth =
    typeof window !== 'undefined' && !isLikelyCrawler() && !isSafari();
  // Default to resolved so public pages paint immediately. Browsers that can
  // silent-check Keycloak do it in the background and only update if needed.
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [hasResolved, setHasResolved] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(initialHint));
  const [firstName, setFirstName] = useState<string | undefined>(
    initialHint?.firstName
  );
  const [lastName, setLastName] = useState<string | undefined>(
    initialHint?.lastName
  );
  const [email, setEmail] = useState<string | undefined>(initialHint?.email);
  const [picture, setPicture] = useState<string | undefined>(
    initialHint?.profilePicture
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const keycloakRef = useRef<PublicKeycloakClient | null>(null);
  const logoutStartedRef = useRef(false);

  useEffect(() => {
    clearStaleKeycloakCallbacks();

    if (!shouldCheckAuth) {
      return;
    }

    let cancelled = false;
    const cancelIdle = runWhenIdle(() => {
      const checkSession = async () => {
        // Public pages should paint immediately. This check only corrects the
        // navbar if another tab/browser flow created a fresh Keycloak session.
        setIsChecking(true);

        try {
          const { createKeycloakClient } = await import('@/config/keycloak');
          const publicKeycloak = createKeycloakClient();
          const authenticated = await publicKeycloak.init({
            pkceMethod: 'S256',
            checkLoginIframe: false,
            onLoad: 'check-sso',
            responseMode: 'query',
            silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
            silentCheckSsoFallback: false,
          });

          if (cancelled) return;

          keycloakRef.current = publicKeycloak;

          const token = publicKeycloak.tokenParsed as
            | {
                given_name?: string;
                family_name?: string;
                email?: string;
                picture?: string;
              }
            | undefined;

          if (authenticated) {
            const nextFirstName = token?.given_name ?? initialHint?.firstName;
            const nextLastName = token?.family_name ?? initialHint?.lastName;
            const nextEmail = token?.email ?? initialHint?.email;
            const nextPicture = token?.picture ?? initialHint?.profilePicture;

            setIsAuthenticated(true);
            setFirstName(nextFirstName);
            setLastName(nextLastName);
            setEmail(nextEmail);
            setPicture(nextPicture);
            setAuthHint({
              firstName: nextFirstName,
              lastName: nextLastName,
              email: nextEmail,
              profilePicture: nextPicture,
              idTokenHint: publicKeycloak.idToken,
            });
          } else {
            setIsAuthenticated(false);
            setFirstName(undefined);
            setLastName(undefined);
            setEmail(undefined);
            setPicture(undefined);
            clearAuthHint();
          }
        } catch (error) {
          if (!cancelled) {
            setIsAuthenticated(false);
            setFirstName(undefined);
            setLastName(undefined);
            setEmail(undefined);
            setPicture(undefined);
            clearAuthHint();
          }
          console.warn('Public auth status check failed:', error);
        } finally {
          if (!cancelled) {
            setIsChecking(false);
            setHasResolved(true);
          }
        }
      };

      checkSession();
    });

    return () => {
      cancelled = true;
      if (typeof cancelIdle === 'function') {
        cancelIdle();
      }
    };
  }, [initialHint, shouldCheckAuth]);

  const login = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.location.href =
      window.location.pathname === ROUTES.CHINOBI_SIGNUP
        ? withPostLoginRedirect(
            ROUTES.LOGIN,
            ROUTES.DASHBOARD_VIDEO_CHAT_CHINOBI
          )
        : ROUTES.LOGIN;
  }, []);

  const getLogoutClient = useCallback(async () => {
    if (keycloakRef.current?.authenticated && keycloakRef.current.token) {
      return keycloakRef.current;
    }

    const keycloakModule = await import('@/config/keycloak');
    const sharedKeycloak = keycloakModule.default as PublicKeycloakClient;

    if (sharedKeycloak.authenticated && sharedKeycloak.token) {
      keycloakRef.current = sharedKeycloak;
      return sharedKeycloak;
    }

    const publicKeycloak = keycloakModule.createKeycloakClient();
    const authenticated = await publicKeycloak.init({
      pkceMethod: 'S256',
      checkLoginIframe: false,
      onLoad: 'check-sso',
      responseMode: 'query',
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      silentCheckSsoFallback: false,
    });

    if (!authenticated || !publicKeycloak.token) return null;

    keycloakRef.current = publicKeycloak;
    return publicKeycloak;
  }, []);

  const logout = useCallback(async () => {
    if (typeof window === 'undefined' || logoutStartedRef.current) return false;

    logoutStartedRef.current = true;
    setIsLoggingOut(true);
    try {
      const keycloak = await getLogoutClient();
      if (!keycloak?.token) return false;

      await userProfileService.logout(keycloak.token);

      try {
        localStorage.removeItem('agentous_tenant_id');
        localStorage.removeItem('agentous_active_agent');
      } catch {
        // Storage restrictions must not interrupt a successful logout.
      }
      clearAuthHint();
      keycloak.clearToken();
      window.location.href = ROUTES.HOME;
      return true;
    } catch (error) {
      console.error('Failed to revoke all Agentous sessions:', error);
      return false;
    } finally {
      logoutStartedRef.current = false;
      setIsLoggingOut(false);
    }
  }, [getLogoutClient]);

  return {
    isAuthenticated,
    isChecking,
    hasResolved,
    firstName,
    lastName,
    email,
    picture,
    isLoggingOut,
    login,
    logout,
  };
};
